import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AdminClientSummary } from '../../clients/index.js'
import type { AdminFacility } from '../index.js'
import { FacilityIcon, WorkersIcon, FacilityAdministratorIcon, Skeleton } from '../../core/index.js'
import { FacilityWorkersModal } from '../../roster/index.js'
import { FacilityDetailModal } from './FacilityDetailModal.js'
import { useDebouncedValue } from '../../roster/index.js'
import { useClientFacilityPages } from '../../clients/index.js'
import type { ComponentProps } from 'react'
import type { FacilitiesAdminApi } from '../adminApi.js'
import type { AccountsAdminApi } from '../../accounts/index.js'
import type { ClientsAdminApi } from '../../clients/index.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = FacilitiesAdminApi & ClientsAdminApi & AccountsAdminApi & ComponentProps<typeof FacilityDetailModal>['api'] & ComponentProps<typeof FacilityWorkersModal>['api']


interface FacilityRow {
  facility: AdminFacility
  client: AdminClientSummary
  /** Names of the facility administrators assigned to this facility. */
  administratorNames: string[]
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const COLUMN_COUNT = 4

interface ClientFacilitiesProps {
  api: AdminApi
  client: AdminClientSummary
  clientId: string
  search: string
  onViewWorkers: (facility: AdminFacility) => void
  onViewFacility: (row: FacilityRow) => void
}

/**
 * One client's part of the table: its name, how many of its facilities
 * are on show, the rows, and a Show more. Each client pages on its own -
 * facilities are stored per client, so there is no one list across clients
 * to page - and draws nothing at all when a search matches none of its
 * facilities.
 */
function ClientFacilities({ api, client, clientId, search, onViewWorkers, onViewFacility }: ClientFacilitiesProps) {
  const { query, facilities, matching, isSearching } = useClientFacilityPages(api, clientId, search)
  // Who administers what, for the client's rows. One read per client, on the key the client's own panel uses.
  const administratorsQuery = useQuery({ queryKey: ['admin', 'facility-administrators', clientId], queryFn: () => api.listFacilityAdministrators(clientId) })
  const administrators = administratorsQuery.data ?? []

  if (query.isPending) {
    return (
      <tbody>
        <tr className="border-b border-marine/10">
          <td colSpan={COLUMN_COUNT} className="px-4 py-3 text-ink-mid">
            <span className="font-bold text-ink">{client.organizationName}</span> &middot; Loading facilities…
          </td>
        </tr>
      </tbody>
    )
  }
  if (query.isError) {
    return (
      <tbody>
        <tr className="border-b border-marine/10">
          <td colSpan={COLUMN_COUNT} className="px-4 py-3 text-coral">
            <span className="font-bold">{client.organizationName}</span> &middot; Couldn't load this client's facilities, so this list is incomplete.
          </td>
        </tr>
      </tbody>
    )
  }
  if (facilities.length === 0) {
    // Nothing to show for this client: no facilities (named in the notice above the table), or none matching the search.
    return null
  }

  return (
    <tbody className={query.isPlaceholderData ? 'opacity-60' : ''}>
      <tr className="border-b border-marine/15 bg-marine-lt/40">
        <th colSpan={COLUMN_COUNT} scope="rowgroup" className="px-4 py-2 text-left">
          <span className="text-xs font-bold text-ink">{client.organizationName}</span>
          <span className="ml-2 text-[11px] font-normal text-ink-mid">
            Showing {facilities.length.toLocaleString()} of {matching.toLocaleString()} {isSearching ? (matching === 1 ? 'matching facility' : 'matching facilities') : matching === 1 ? 'facility' : 'facilities'}
          </span>
        </th>
      </tr>
      {facilities.map((facility) => {
        const administratorNames = administrators
          .filter((administrator) => administrator.facilityIds.includes(facility.facilityId))
          .map((administrator) => `${administrator.firstName} ${administrator.lastName}`)
          .sort((a, b) => a.localeCompare(b))
        const workerCount = facility.workerCount ?? 0
        const workerLabel = `${workerCount.toLocaleString()} ${workerCount === 1 ? 'worker' : 'workers'} on the roster`
        return (
          <tr key={facility.facilityId} className="border-b border-marine/10 align-top">
            <td className="px-4 py-3">
              <div className="flex items-center gap-3">
                <FacilityIcon className="w-5 h-5 text-marine" />
                <div>
                  <button
                    type="button"
                    onClick={() => onViewFacility({ facility, client, administratorNames })}
                    className="font-bold text-ink text-left underline decoration-marine/30 underline-offset-2 hover:text-marine hover:decoration-marine"
                  >
                    {facility.name}
                  </button>
                </div>
              </div>
            </td>
            <td className="px-4 py-3">
              <button
                type="button"
                onClick={() => onViewWorkers(facility)}
                title={`${workerLabel} - view them`}
                aria-label={`${workerLabel} at ${facility.name} - view them`}
                className="inline-flex items-center gap-1 font-bold text-marine hover:text-marine-dk"
              >
                <WorkersIcon />
                {workerCount.toLocaleString()}
              </button>
            </td>
            <td className="px-4 py-3 text-ink">
              {administratorsQuery.isPending ? (
                <span className="text-ink-mid">Loading…</span>
              ) : administratorNames.length === 0 ? (
                <span className="text-ink-mid">No administrator assigned</span>
              ) : (
                <ul className="space-y-0.5">
                  {administratorNames.map((name) => (
                    <li key={name} className="flex items-center gap-2">
                      <FacilityAdministratorIcon className="w-3.5 h-3.5 text-marine" />
                      {name}
                    </li>
                  ))}
                </ul>
              )}
            </td>
            <td className="px-4 py-3 text-ink-mid whitespace-nowrap">{formatDate(facility.createdAt)}</td>
          </tr>
        )
      })}
      {query.hasNextPage && (
        <tr className="border-b border-marine/10">
          <td colSpan={COLUMN_COUNT} className="px-4 py-2">
            <button type="button" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage} className="text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-50">
              {query.isFetchingNextPage ? 'Loading…' : `Show more of ${client.organizationName}`}
            </button>
          </td>
        </tr>
      )}
    </tbody>
  )
}

/**
 * Every client's facilities, client by client: each client's in name
 * order, a few to begin with and more on request, with one box that finds
 * facilities by the start of their name across all of them. The same
 * paging and search as a client's own panel and the operator's list, on
 * the same cache. The worker count opens the roster modal; the name opens
 * the facility's detail.
 */
export function AdminFacilityList({ api }: { api: AdminApi }) {
  const [viewingWorkersOf, setViewingWorkersOf] = useState<AdminFacility | null>(null)
  const [viewingFacility, setViewingFacility] = useState<FacilityRow | null>(null)
  const [searchText, setSearchText] = useState('')
  const search = useDebouncedValue(searchText)
  const clientsQuery = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  // Only an active client has a clientId, which is what facilities hang off.
  const activeClients = (clientsQuery.data ?? [])
    .flatMap((client) => (client.clientId ? [{ client, clientId: client.clientId }] : []))
    .sort((a, b) => a.client.organizationName.localeCompare(b.client.organizationName))
  const totalFacilities = activeClients.reduce((sum, { client }) => sum + (client.facilityCount ?? 0), 0)
  const clientsWithoutFacilities = activeClients.filter(({ client }) => (client.facilityCount ?? 0) === 0).map(({ client }) => client)

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      <div className="text-sm text-ink-mid mb-4">
        {clientsQuery.isSuccess &&
          `${totalFacilities.toLocaleString()} ${totalFacilities === 1 ? 'facility' : 'facilities'} across ${activeClients.length} active ${activeClients.length === 1 ? 'client' : 'clients'}`}
      </div>

      {clientsWithoutFacilities.length > 0 && (
        <div className="border border-gold bg-gold-lt px-4 py-3 mb-4 text-xs text-ink">
          No facilities yet: {clientsWithoutFacilities.map((client) => client.organizationName).join(', ')}. A facility is the operator's to create, from
          their own dashboard; or import them from the client's panel under Clients.
        </div>
      )}

      {clientsQuery.isPending ? (
        <Skeleton lines={5} label="Loading facilities" />
      ) : clientsQuery.isError ? (
        <LoadFailure message="Failed to load clients. Confirm the API is reachable." onRetry={() => void clientsQuery.refetch()} retrying={clientsQuery.isFetching} />
      ) : totalFacilities === 0 ? (
        <div className="bg-white border border-marine/15 p-8 text-sm text-ink-mid">
          <span className="font-bold text-ink">No facilities set up.</span> A facility is the operator's record: they register it from their own dashboard
          (New facility), and every client needs at least one before it can have workers. SeeWhoCares can also import a facilities file from the client's
          panel.
        </div>
      ) : (
        <>
          <label htmlFor="admin-facility-search" className="sr-only">
            Find a facility
          </label>
          <input
            id="admin-facility-search"
            type="search"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Find a facility by the start of its name, in any client"
            autoComplete="off"
            className="mb-3 w-full border border-marine/25 bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-marine"
          />
          <div className="bg-white border border-marine/15 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
                  <th className="px-4 py-2 font-bold">Facility</th>
                  <th className="px-4 py-2 font-bold">Workers</th>
                  <th className="px-4 py-2 font-bold">Administrator</th>
                  <th className="px-4 py-2 font-bold">Registered</th>
                </tr>
              </thead>
              {activeClients
                .filter(({ client }) => (client.facilityCount ?? 0) > 0)
                .map(({ client, clientId }) => (
                  <ClientFacilities key={clientId} api={api} client={client} clientId={clientId} search={search} onViewWorkers={setViewingWorkersOf} onViewFacility={setViewingFacility} />
                ))}
            </table>
          </div>
          {search.trim() !== '' && <p className="mt-2 text-[11px] text-ink-mid">A client with no facility whose name starts with “{search.trim()}” is not shown.</p>}
        </>
      )}

      {viewingWorkersOf && <FacilityWorkersModal api={api} facility={viewingWorkersOf} onClose={() => setViewingWorkersOf(null)} />}
      {viewingFacility && (
        <FacilityDetailModal
          api={api}
          facility={viewingFacility.facility}
          client={viewingFacility.client}
          administratorNames={viewingFacility.administratorNames}
          onClose={() => setViewingFacility(null)}
        />
      )}
    </div>
  )
}
